import React, { useMemo } from "react";
import { View, StyleSheet, useWindowDimensions, ViewStyle } from "react-native";
import RText from "../../RText";
import variables from "../../../styles/variables";
import Icon from "../../Icon";
import Layout from "../../Layout/Layout";
import RTVGradient from "../../Gradients/RTVGradient";

const { colors, spacing } = variables;

// If the data request was successfully fulfilled but there
// is no content to display, display this component

type Props = {
  message?: string;
  style?: ViewStyle;
  iconType?: string | null;
  component?: React.ReactNode;
  inLayout?: boolean;
  dark?: boolean;
  existingUserState?: boolean;
};

const EmptyState: React.FC<Props> = ({
  message,
  style = { marginVertical: spacing.small },
  iconType = "plug",
  component,
  inLayout = false,
  dark = false,
  existingUserState = false,
}) => {
  const { width } = useWindowDimensions();
  const isSmallScreen = width < 567;

  const content = useMemo(() => {
    const backgroundColor = dark ? "transparent" : colors.palette.neutral.x100;

    const iconFill = () => {
      if (existingUserState) return "palette.rtv.primary";
      if (dark) return "palette.neutral.x0";
      return "palette.neutral.x500";
    };

    const color = () => {
      if (existingUserState) return colors.palette.rtv.primary;
      if (dark) return "white";
      return "grey";
    };

    return (
      <View
        style={[
          style,
          styles.container,
          isSmallScreen && !dark ? { aspectRatio: 1 } : {},
          { justifyContent: style.justifyContent || "center" },
          { alignItems: style.alignItems || "center" },
          existingUserState ? { minHeight: 300 } : { minHeight: 200 },
          { backgroundColor },
        ]}
      >
        {iconType ? (
          <Icon name={iconType} size="xxxlarge" fill={iconFill()} />
        ) : null}
        <RText
          family="secondary"
          weight="bold"
          color={color()}
          uppercase
          style={[styles.text, style]}
        >
          {message || "No content to display"}
        </RText>
        {component}
      </View>
    );
  }, [iconType, dark, message, component, isSmallScreen, style]);

  if (existingUserState)
    return (
      <RTVGradient boxMode>
        <View style={{ paddingHorizontal: 7 }}>{content}</View>
      </RTVGradient>
    );

  if (inLayout) return <Layout headingLoader={false} content={content} />;

  return content;
};

export default EmptyState;

const styles = StyleSheet.create({
  container: {
    paddingVertical: spacing.medium,
    width: "100%",
  },
  text: { textAlign: "center" },
});
